import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import { getPlaceholderData, resolvePlaceholder } from 'modules/manager/domain/placeholder';
import type { PlaceholderFacade, ResolvedValue, ResolverContext } from 'modules/manager/domain/placeholder';
import { isPlaceholderKeyValidFor, TemplateType } from 'modules/manager/domain/template-type';

/**
 * Puts the real values into a template's placeholders on the canvas. A placeholder that has no value (a key the type
 * does not allow, no data, an image that fails to load even by its fallback) is removed, so the frame never shows
 * the editor's dashed cue. The media manager did the same before its render moved to the backend (`77da087f`).
 */
export async function substitutePlaceholders(editor: FabricEditor, ctx: ResolverContext, templateType?: TemplateType): Promise<void> {
  const facade = editor.placeholders;
  if (!facade) return;
  const groups = editor.canvas.getObjects().filter((object): object is fabric.Group => facade.isPlaceholder(object));
  const tasks: Promise<void>[] = [];
  for (const group of groups) {
    const data = getPlaceholderData(group);
    if (!data) continue;
    const value = templateType && !isPlaceholderKeyValidFor(templateType, data.placeholderKey) ? null : resolvePlaceholder(data, ctx);
    if (!value) {
      editor.canvas.remove(group);
      continue;
    }
    tasks.push(applyWithFallback(editor, facade, group, value));
  }
  await Promise.all(tasks);
}

async function applyWithFallback(
  editor: FabricEditor,
  facade: PlaceholderFacade,
  group: fabric.Group,
  value: ResolvedValue,
): Promise<void> {
  if (await facade.applyResolved(group, value)) return;
  const fallbackUrl = value.type === 'image' ? value.fallbackUrl : undefined;
  if (fallbackUrl && (await facade.applyResolved(group, { type: 'image', url: fallbackUrl }))) return;
  editor.canvas.remove(group);
}
